import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDbWake } from '../hooks/useDbWake';
import { DbWakeBanner } from '../components/DbWakeBanner';

/** Microsoft logo colors — brand-required values, must NOT be replaced with cu2 tokens */
const MS_LOGO = { // MS_LOGO
  red:    '#f25022', // MS_LOGO
  green:  '#7fba00', // MS_LOGO
  blue:   '#00a4ef', // MS_LOGO
  yellow: '#ffb900', // MS_LOGO
} as const;

/**
 * LoginPage — Entra ID SSO entry point + passcode demo sign-in.
 *
 * Production: redirects to Azure Entra ID OIDC authorization endpoint.
 * Demo / dev: asks crm-api for a REAL 8-hour staff JWT via POST /api/auth/demo-login
 * (shared passcode, role picked here). The token lands in localStorage as `crm_dev_token`,
 * which is what every page in the app already sends as its Bearer token.
 *
 * Opening this page also wakes the CRM database (useDbWake → POST /api/system/wake): it is stopped
 * every night and started on demand. Sign-in waits until it answers, so nobody lands on empty pages.
 *
 * History: this page used to mint an UNSIGNED mock JWT client-side. The UI then looked signed in
 * while every API call returned 401, because the API verifies HS256 against JWT_SECRET.
 */

/** Microsoft sign-in is a server-side OIDC flow on crm-api; the button simply navigates there. */
const ENTRA_LOGIN_PATH = '/api/auth/entra/login';

/** Error codes crm-api's Entra callback can send back on /login?error=… */
const SSO_ERRORS: Record<string, string> = {
  sso_not_configured: 'Microsoft sign-in is not configured on this environment.',
  sso_state: 'Sign-in session expired or was tampered with. Try again.',
  sso_no_code: 'Microsoft did not return a sign-in code. Try again.',
  sso_no_role: 'Your Microsoft account is signed in but has no CRM role assigned. Ask an admin to assign one.',
  sso_failed: 'Microsoft sign-in could not be completed. Try again or use the demo sign-in.',
  access_denied: 'Microsoft sign-in was cancelled.',
};

const MOCK_USERS = [
  { label: 'Admin', role: 'admin' },
  { label: 'Loan Officer', role: 'loan_officer' },
  { label: 'Marketing Staff', role: 'marketing_staff' },
  { label: 'Compliance Officer', role: 'compliance_officer' },
] as const;

interface DemoLoginResponse {
  token: string;
  expiresAt: string;
  role: string;
}

export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const [selectedRole, setSelectedRole] = useState<(typeof MOCK_USERS)[number]['role']>('admin');
  const [passcode, setPasscode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [demoEnabled, setDemoEnabled] = useState<boolean | null>(null);
  const [entraEnabled, setEntraEnabled] = useState<boolean | null>(null);
  const [cuName, setCuName] = useState<string>('');
  const wake = useDbWake();
  // Sign-in itself never needs the database, but every page after it does. Hold the buttons while it
  // starts; if it cannot be started here (unknown/timeout) let people through — the banner says why.
  const waiting = wake.state === 'checking' || wake.state === 'starting';
  // Re-ask the sign-in questions once the wait ends: a cold crm-api may not have answered the first time.
  const apiSettled = !waiting;

  // Surface an SSO error the API redirected back with (e.g. /login?error=sso_no_role).
  useEffect(() => {
    const code = new URLSearchParams(window.location.search).get('error');
    if (code !== null) setError(SSO_ERRORS[code] ?? `Sign-in failed (${code}).`);
  }, []);

  // Ask the API whether Microsoft sign-in is configured; the button only shows when it is.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/entra/enabled', { method: 'POST' });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { enabled: boolean };
        if (!cancelled) setEntraEnabled(data.enabled);
      } catch {
        if (!cancelled) setEntraEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiSettled]);

  // Ask the API whether demo sign-in is enabled here; hide the form on tenants where it is off.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch('/api/auth/demo-login/enabled', { method: 'POST' });
        if (!res.ok) throw new Error(String(res.status));
        const data = (await res.json()) as { enabled: boolean; cuName?: string };
        if (!cancelled) { setDemoEnabled(data.enabled); setCuName(data.cuName ?? ''); }
      } catch {
        if (!cancelled) setDemoEnabled(false);
      }
    })();
    return () => { cancelled = true; };
  }, [apiSettled]);

  function handleEntraLogin(): void {
    if (waiting) return;
    window.location.href = ENTRA_LOGIN_PATH;
  }

  async function handleDemoLogin(e?: React.FormEvent): Promise<void> {
    e?.preventDefault();
    if (busy || waiting) return;
    setError(null);
    if (passcode.trim().length === 0) { setError('Enter the demo passcode.'); return; }
    setBusy(true);
    try {
      const res = await fetch('/api/auth/demo-login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: selectedRole, passcode: passcode.trim() }),
      });
      if (res.status === 401) { setError('That passcode was not accepted.'); return; }
      if (res.status === 429) { setError('Too many attempts. Wait a minute and try again.'); return; }
      if (res.status === 404) { setError('Demo sign-in is not enabled on this environment.'); return; }
      if (!res.ok) { setError(`Sign-in failed (HTTP ${res.status}).`); return; }
      const data = (await res.json()) as DemoLoginResponse;
      localStorage.setItem('crm_dev_token', data.token);
      void navigate('/dashboard', { replace: true }); // same landing as the router's authenticated default
      // Force page reload so AuthProvider re-reads localStorage
      window.location.reload();
    } catch {
      setError('Could not reach the API. Try again in a moment.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-bg">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/cu2-logo.svg" alt="CU 2.0" width={120} height={40} className="mx-auto mb-6 h-10 w-auto" />
          <h1 className="font-heading text-2xl font-extrabold text-navy">CRM</h1>
          <p className="text-sm text-ink-muted mt-1">Sign in to access your credit union dashboard</p>
        </div>

        <div className="mb-4">
          <DbWakeBanner wake={wake} />
        </div>

        <div className="bg-surface rounded-lg shadow-sm border border-line border-t-[3px] border-t-brand p-8">
          {/* Microsoft SSO Button — only when crm-api has Entra configured */}
          {entraEnabled === true && (
          <button
            type="button"
            onClick={handleEntraLogin}
            disabled={waiting}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border-[1.5px] border-line-strong rounded-md font-heading text-sm font-bold text-navy shadow-xs hover:border-brand hover:text-brand-dark transition-colors mb-4 disabled:opacity-60 disabled:cursor-wait"
          >
            {/* Microsoft logo */}
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="1" y="1" width="9" height="9" fill={MS_LOGO.red} />
              <rect x="11" y="1" width="9" height="9" fill={MS_LOGO.green} />
              <rect x="1" y="11" width="9" height="9" fill={MS_LOGO.blue} />
              <rect x="11" y="11" width="9" height="9" fill={MS_LOGO.yellow} />
            </svg>
            Sign in with Microsoft
          </button>
          )}
          {!waiting && entraEnabled === false && demoEnabled === false && (
            <p role="alert" className="text-sm text-ink-soft text-center">No sign-in method is configured on this environment.</p>
          )}

          {/* Dev login section */}
          {demoEnabled !== false && (
            <>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-line" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-surface px-3 font-heading font-bold tracking-[0.08em] text-ink-muted">DEMO SIGN-IN</span>
                </div>
              </div>

              <form className="space-y-3" onSubmit={(e) => { void handleDemoLogin(e); }}>
                <p className="text-xs text-ink-muted text-center">
                  {cuName ? `Demo tenant: ${cuName}. ` : ''}Pick a role and enter the shared demo passcode.
                </p>

                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as (typeof MOCK_USERS)[number]['role'])}
                  className="w-full px-3 py-2 text-sm border border-line-strong rounded-md bg-surface text-navy focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand"
                >
                  {MOCK_USERS.map((user) => (
                    <option key={user.role} value={user.role}>
                      {user.label}
                    </option>
                  ))}
                </select>

                <input
                  type="password"
                  autoComplete="off"
                  value={passcode}
                  onChange={(e) => setPasscode(e.target.value as string)}
                  placeholder="Demo passcode"
                  aria-label="Demo passcode"
                  className="w-full px-3 py-2 text-sm border border-line-strong rounded-md bg-surface text-navy focus:outline-none focus:ring-2 focus:ring-brand/50 focus:border-brand"
                />

                {error && (
                  <p role="alert" className="text-xs text-fail text-center">{error}</p>
                )}

                <button
                  type="submit"
                  disabled={busy || waiting}
                  className="w-full px-4 py-2.5 font-heading text-sm font-bold text-on-brand rounded-md shadow-brand transition-colors bg-brand hover:bg-brand-hover disabled:opacity-60 disabled:shadow-none"
                >
                  {waiting ? 'Waiting for the database…' : busy ? 'Signing in…' : `Sign in as ${MOCK_USERS.find((u) => u.role === selectedRole)?.label ?? 'demo user'}`}
                </button>
              </form>
            </>
          )}
        </div>

        <p className="text-center text-xs text-ink-muted mt-6">
          CU 2.0 CRM — secured by Microsoft Entra ID
        </p>
      </div>
    </div>
  );
}
